import { useCallback, useMemo, useRef } from 'react';
import { Field, TextInput } from '@carboledger/ui';
import { Calendar } from 'lucide-react';
import { useTranslations } from '@/hooks';
import styles from './DateFields.module.scss';

// TODO: swap both for a DatePicker when the library has one.

/**
 * Props:
 *   onClick   opens the browser's date picker
 *   disabled  matches the input's own disabled state
 */
const CalendarButton = ({ onClick, disabled }) => {
  const t = useTranslations();

  return (
    <button
      type="button"
      className={styles.pickerTrigger}
      aria-label={t('COMMON_OPEN_DATE_PICKER')}
      disabled={disabled}
      onClick={onClick}
    >
      <Calendar size={20} aria-hidden="true" />
    </button>
  );
};

// The native indicator is hidden, so the suffix button is what opens the picker.
const useDatePicker = (disabled) => {
  const inputRef = useRef(null);
  const openPicker = useCallback(() => inputRef.current?.showPicker?.(), []);
  // Inside Field's render prop, so a fresh element every keystroke without the memo.
  const suffix = useMemo(
    () => <CalendarButton onClick={openPicker} disabled={disabled} />,
    [openPicker, disabled],
  );
  return { inputRef, suffix };
};

/**
 * Props:
 *   fieldName  the form key
 *   label      the field's own label — the two named exports below pin theirs
 *   required   marks it and adds the generic required rule
 *   rules      replaces that generic rule where a screen specifies its own message
 *   disabled   locks the control while only other fields may change
 */
export const DateField = ({ fieldName, label, required = false, rules, disabled = false }) => {
  const { inputRef, suffix } = useDatePicker(disabled);

  return (
    <Field name={fieldName} required={required && !rules} rules={rules}>
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <TextInput
          ref={inputRef}
          className={styles.dateInput}
          id={id}
          name={name}
          type="date"
          label={label}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={required}
          disabled={disabled}
          suffix={suffix}
        />
      )}
    </Field>
  );
};

/**
 * Props:
 *   label      overrides the plain field name, for a form whose deadline reads differently
 *   fieldName  form key, so a screen with several shipment rows can index them
 *   required   defaults to true; a conditionally-required date passes false and owns `rules`
 *   rules      replaces the generic required message where a screen specifies its own
 *   disabled   locks the control while only other fields may change
 */
export const DateOfDispatchField = ({
  label,
  fieldName = 'dateOfDispatch',
  required = true,
  rules,
  disabled = false,
}) => {
  const t = useTranslations();
  const { inputRef, suffix } = useDatePicker(disabled);

  // Own rules replace Field's `required`, whose message is generic; the mark stays either way.
  return (
    <Field name={fieldName} required={required && !rules} rules={rules}>
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <TextInput
          ref={inputRef}
          className={styles.dateInput}
          id={id}
          name={name}
          type="date"
          label={label ?? t('FIELD_DATE_OF_DISPATCH')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={required}
          disabled={disabled}
          suffix={suffix}
        />
      )}
    </Field>
  );
};

/**
 * Props:
 *   rules     RHF rules; the shipment form compares it against the dispatch date
 *   required  the declaration form requires it; the shipment/receive forms do not
 *   disabled  locks the control while only other fields may change
 */
export const DateOfDischargeField = ({ rules, required = false, disabled = false }) => {
  const t = useTranslations();
  const { inputRef, suffix } = useDatePicker(disabled);

  return (
    <Field name="dateOfDischarge" required={required} rules={rules}>
      {({ id, name, value, onChange, onBlur, status, message, required: isRequired }) => (
        <TextInput
          ref={inputRef}
          className={styles.dateInput}
          id={id}
          name={name}
          type="date"
          label={required ? t('FIELD_DATE_OF_DISCHARGE') : t('SHIPMENT_FORM_DATE_OF_DISCHARGE')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={isRequired}
          disabled={disabled}
          suffix={suffix}
        />
      )}
    </Field>
  );
};

/**
 * Props:
 *   value      `yyyy-MM-dd`, the native input's own format
 *   onChange   (event) => void
 *   ariaLabel  the control's accessible name — a toolbar input has no visible label
 *   status     error when the screen rejects the value
 *   disabled   locks the control
 *   filled     the grey 32px box a table toolbar uses, instead of the bordered field
 */
// Not form-bound: a toolbar filter, e.g. a report's date range, owns its own state.
export const DateInput = ({
  value,
  onChange,
  ariaLabel,
  status,
  disabled = false,
  filled = false,
}) => {
  const { inputRef, suffix } = useDatePicker(disabled);

  return (
    <TextInput
      ref={inputRef}
      className={filled ? styles.filledDateInput : styles.dateInput}
      type="date"
      value={value}
      onChange={onChange}
      status={status}
      disabled={disabled}
      suffix={suffix}
      appearance={filled ? 'filled' : 'field'}
      aria-label={ariaLabel}
    />
  );
};
